import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal } from '../common/Modal'
import { Input } from '../common/Input'
import { Button } from '../common/Button'
import { signupSchema, SignupFormData } from '../../utils/validation'
import { tenantService } from '../../services/tenantService'
import { authService } from '../../services/authService'
import { FiLoader, FiAlertCircle } from 'react-icons/fi'

interface SignupFormProps {
  isOpen: boolean
  onClose: () => void
}

export const SignupForm = ({ isOpen, onClose }: SignupFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'form' | 'processing' | 'redirect'>('form')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  })

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      // Tentar criar tenant na API
      try {
        setStep('processing')
        
        await tenantService.createTenant({
          name: data.name,
          email: data.email,
          password: data.password,
          passwordConfirmation: data.passwordConfirmation,
          acceptTerms: data.acceptTerms,
        })

        // Após criar o tenant, faz login usando email e senha
        try {
          setStep('processing')
          
          // Faz login com as credenciais informadas
          const loginResponse = await authService.login(data.email, data.password)
          
          if (loginResponse.token && loginResponse.user && loginResponse.tenant) {
            // Salva token e dados do usuário temporariamente para passar para a aplicação
            sessionStorage.setItem('projetoize_signup_token', loginResponse.token)
            sessionStorage.setItem('projetoize_signup_user', JSON.stringify({
              ...loginResponse.user,
              role: loginResponse.tenant.role || 'owner',
              tenantId: loginResponse.tenant.id
            }))
            
            // Se houver informações de trial/pagamento, também salva
            if (loginResponse.needs_payment !== undefined) {
              sessionStorage.setItem('projetoize_signup_needs_payment', String(loginResponse.needs_payment))
            }
            if (loginResponse.trial_ends_at) {
              sessionStorage.setItem('projetoize_signup_trial_ends_at', loginResponse.trial_ends_at)
            }
            if (loginResponse.billing_status) {
              sessionStorage.setItem('projetoize_signup_billing_status', loginResponse.billing_status)
            }
            
            setStep('redirect')
            
            // Redireciona para a aplicação web após 1 segundo
            setTimeout(() => {
              const appUrl = import.meta.env.VITE_APP_URL || 'http://localhost:5174'
              window.location.href = appUrl
            }, 1000)
          } else {
            setError('Conta criada com sucesso, mas houve um erro ao fazer login. Por favor, faça login manualmente.')
            setStep('form')
          }
        } catch (_loginError: unknown) {
          // Se falhar o login, ainda informa que a conta foi criada
          setError('Conta criada com sucesso, mas houve um erro ao fazer login automaticamente. Por favor, faça login manualmente com seu email e senha.')
          setStep('form')
        }
      } catch (createError: unknown) {
        // Se o erro for 409 (email já existe), verifica se há tenant associado
        const error = createError as { response?: { status?: number }; message?: string }
        if (error.response?.status === 409 || error.message?.includes('já existe')) {
          setError('Este email já está cadastrado. Por favor, faça login ou use outro email.')
          setStep('form')
        } else {
          // Outro tipo de erro
          throw createError
        }
      }
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error.message || 'Erro ao criar conta. Tente novamente.')
      setStep('form')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      reset()
      setError(null)
      setStep('form')
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Criar Conta" size="lg">
      {step === 'form' && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <Input
            label="Nome da Empresa/Organização"
            placeholder="Ex: Minha Empresa"
            {...register('name')}
            error={errors.name?.message}
            disabled={isLoading}
          />

          <Input
            label="Email do Administrador"
            type="email"
            placeholder="seu@email.com"
            {...register('email')}
            error={errors.email?.message}
            disabled={isLoading}
          />

          <Input
            label="Senha"
            type="password"
            placeholder="Mínimo 8 caracteres"
            {...register('password')}
            error={errors.password?.message}
            disabled={isLoading}
          />

          <Input
            label="Confirmar Senha"
            type="password"
            placeholder="Digite a senha novamente"
            {...register('passwordConfirmation')}
            error={errors.passwordConfirmation?.message}
            disabled={isLoading}
          />

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="acceptTerms"
              {...register('acceptTerms')}
              disabled={isLoading}
              className="mt-1"
            />
            <label htmlFor="acceptTerms" className="text-sm text-gray-700">
              Eu aceito os{' '}
              <a href="/termos" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                termos de uso
              </a>{' '}
              e a{' '}
              <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                política de privacidade
              </a>
            </label>
          </div>
          {errors.acceptTerms && (
            <p className="text-sm text-red-600">{errors.acceptTerms.message}</p>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <FiLoader className="animate-spin mr-2" />
                  Processando...
                </>
              ) : (
                'Avançar'
              )}
            </Button>
          </div>
        </form>
      )}

      {step === 'processing' && (
        <div className="text-center py-8">
          <FiLoader className="animate-spin mx-auto mb-4 text-primary-600" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Criando sua conta...
          </h3>
          <p className="text-gray-600">
            Aguarde enquanto configuramos tudo para você.
          </p>
        </div>
      )}

      {step === 'redirect' && (
        <div className="text-center py-8">
          <div className="text-6xl mb-4">✅</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Conta criada com sucesso!
          </h3>
          <p className="text-gray-600 mb-4">
            Redirecionando para a plataforma...
          </p>
          <FiLoader className="animate-spin mx-auto text-primary-600" size={24} />
        </div>
      )}
    </Modal>
  )
}

